// VenueComparisonPanel — the collapsible cross-venue market-overview at the very
// top of the page (above the venue selector). Expand for the multi-venue
// overview; collapse to focus on the single-venue deep-dive below. Independent
// of the venue selector — always shows ALL venues. Collapse state persists in
// localStorage. Renders nothing when the feed carries no venue_comparison.

import { useContext, useMemo } from 'react';
import { Box, Stack, Collapse } from '@mui/material';
import { faChevronDown } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { tokens } from '../../../../styles/tokens';
import usePersistentState from '../../../../providers/use-persistent-state';
import {
  dilettaPanelSx,
  DILETTA_TITLE,
} from '../../../../components/v2/widgets/exotics-shared/diletta-chrome';
import { OptionsFlowContext } from '../../../../providers/options-flow/options-flow-provider';
import { resolveVenueComparisonWindow } from '../../../../providers/options-flow/options-flow.helpers';
import { VenueComparisonReport } from '../../../../providers/options-flow/types';
import { VenueMixChart } from '../venue-mix-chart';
import { VenueComparisonTable } from '../venue-comparison-table';

export const VenueComparisonPanel = ({ report }: { report: VenueComparisonReport | null }) => {
  const { activeWindow } = useContext(OptionsFlowContext);
  const [expanded, setExpanded] = usePersistentState<boolean>(
    ['OPTIONS_FLOW'],
    'venueComparisonExpanded',
    true,
  );

  // Follow the SAME global window as the rest of the page — the panel re-renders
  // in lockstep when the window switches (no separate control). Falls back to an
  // available window if the report lacks the active one.
  const slice = useMemo(
    () => resolveVenueComparisonWindow(report, activeWindow),
    [report, activeWindow],
  );

  // Stale (t-1) venues to badge — only present on the 24h window, where alts
  // haven't posted today's data (their summary share is a latency artifact).
  const tMinusOne = useMemo(
    () => new Set(slice?.meta?.stale_venues ?? []),
    [slice?.meta?.stale_venues],
  );

  // Self-hide when the feed doesn't carry the report / has nothing for any window.
  if (!report || !slice) return null;

  // A degenerate/absent series (e.g. 24h table-only) -> render the table without
  // a misleading empty/1-bar chart.
  const showBar = (slice.series?.length ?? 0) >= 2;

  // Range from the ACTIVE window's meta (the report-level meta has no range).
  const from = slice.meta?.window_from ?? report.meta?.window_from;
  const to = slice.meta?.window_to ?? report.meta?.window_to;
  const range = from && to ? `${from} → ${to}` : undefined;

  return (
    <Box sx={{ ...dilettaPanelSx, padding: '12px 16px 14px', mb: 2 }}>
      <Stack
        direction="row"
        alignItems="center"
        onClick={() => setExpanded((v) => !v)}
        role="button"
        aria-expanded={expanded}
        sx={{ cursor: 'pointer', userSelect: 'none' }}
      >
        <Box sx={{ fontSize: 13, fontWeight: 700, color: DILETTA_TITLE }}>
          {report.title || 'Cross-venue comparison'}
        </Box>
        {range && (
          <Box
            sx={{
              ml: 1.25,
              fontSize: 10.5,
              fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
              color: tokens.text.muted,
            }}
          >
            {range}
          </Box>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <FontAwesomeIcon
          icon={faChevronDown}
          style={{
            fontSize: 12,
            color: tokens.text.muted,
            transition: 'transform 0.2s ease',
            transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
          }}
        />
      </Stack>

      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ mt: 1.5 }}>
          {showBar && slice.series && (
            <VenueMixChart
              series={slice.series}
              measures={report.measures}
              granularity={slice.granularity}
            />
          )}
          {slice.summary.length > 0 && (
            <VenueComparisonTable
              summary={slice.summary}
              tMinusOne={tMinusOne}
              coverageNote={slice.meta?.coverage_note}
            />
          )}
        </Box>
      </Collapse>
    </Box>
  );
};
